import { useState, useEffect } from "react";
import { NavLink, Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LogOut, User, Crown, Menu, X } from "lucide-react";

const LINKS = [
  ["/studio", "Studio", "nav-studio-link"],
  ["/gallery", "Gallery", "nav-gallery-link"],
  ["/pricing", "Pricing", "nav-pricing-link"],
];

export default function Nav() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const isAuthed = user && user !== false;

  useEffect(() => setOpen(false), [pathname]);
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const handleLogout = async () => {
    await logout();
    setOpen(false);
    nav("/");
  };

  return (
    <header
      data-testid="site-nav"
      className="sticky top-0 z-50 border-b border-[#222] bg-[#050505]/85 backdrop-blur-xl backdrop-saturate-150"
    >
      <div className="mx-auto flex max-w-[1400px] items-center justify-between px-4 py-3 md:px-6 md:py-4">
        <Link to="/" data-testid="nav-home-link" className="font-display text-2xl tracking-widest">
          <span className="text-[#FF003C]">V</span>OLT<span className="text-[#FFE800]">.</span>MV
        </Link>

        <nav className="hidden gap-2 md:flex">
          {LINKS.map(([to, label, tid]) => (
            <NavLink
              key={to}
              to={to}
              data-testid={tid}
              className={({ isActive }) => `btn-ghost ${isActive ? "border-[#FF003C] text-[#FF003C]" : ""}`}
            >
              {label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          {isAuthed ? (
            <>
              <div className="hidden items-center gap-2 border border-[#222] px-3 py-1.5 font-mono text-xs uppercase tracking-widest text-[#c8c8c8] md:flex" data-testid="nav-user-badge">
                <User size={12} className="text-[#FFE800]" />
                {user.name || user.email}
              </div>
              {user.is_pro ? (
                <span data-testid="nav-pro-badge" className="flex items-center gap-1 bg-[#FFE800] px-2 py-1.5 font-mono text-[10px] uppercase tracking-widest text-black">
                  <Crown size={12} /> Pro
                </span>
              ) : (
                <Link to="/pricing" data-testid="nav-go-pro-link" className="flex items-center gap-1 border border-[#FF003C] px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-widest text-[#FF003C] transition-colors hover:bg-[#FF003C] hover:text-white md:px-3 md:text-xs">
                  <Crown size={12} /> Go Pro
                </Link>
              )}
              <button data-testid="nav-logout-btn" onClick={handleLogout} className="btn-ghost !hidden md:!inline-flex">
                <LogOut size={14} /> Log Out
              </button>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login-link" className="btn-ghost !hidden md:!inline-flex">
                Sign In
              </Link>
              <Link to="/register" data-testid="nav-register-link" className="btn-brutal !px-4 !py-2.5 md:!px-6 md:!py-3.5">
                Get Started
              </Link>
            </>
          )}
          <button
            data-testid="nav-menu-toggle"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            onClick={() => setOpen((o) => !o)}
            className="flex h-10 w-10 items-center justify-center border border-[#222] text-white transition-colors hover:border-[#FF003C] md:hidden"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="nav-mobile-menu"
          className="absolute inset-x-0 top-full h-[calc(100dvh-57px)] overflow-y-auto border-t border-[#222] bg-[#050505] md:hidden"
        >
          <div className="flex flex-col">
            {LINKS.map(([to, label, tid], i) => (
              <NavLink
                key={to}
                to={to}
                data-testid={`mobile-${tid}`}
                style={{ animationDelay: `${i * 40}ms` }}
                className={({ isActive }) =>
                  `mobile-nav-item flex items-center justify-between border-b border-[#222] px-5 py-5 font-display text-3xl tracking-wide ${isActive ? "text-[#FF003C]" : "text-white"}`
                }
              >
                {label}
                <span className="font-mono text-xs text-[#808080]">// 0{i + 1}</span>
              </NavLink>
            ))}
          </div>

          <div className="p-5">
            {isAuthed ? (
              <>
                <div className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-[#c8c8c8]" data-testid="mobile-nav-user">
                  <User size={12} className="text-[#FFE800]" /> {user.name || user.email}
                  {user.is_pro && <span className="ml-auto bg-[#FFE800] px-2 py-0.5 text-[10px] text-black">PRO</span>}
                </div>
                {!user.is_pro && (
                  <Link to="/pricing" data-testid="mobile-nav-go-pro" className="btn-brutal mt-5 w-full">
                    <Crown size={16} /> Go Pro · $15.99/mo
                  </Link>
                )}
                <button data-testid="mobile-nav-logout-btn" onClick={handleLogout} className="btn-brutal secondary mt-3 w-full">
                  <LogOut size={16} /> Log Out
                </button>
              </>
            ) : (
              <>
                <Link to="/register" data-testid="mobile-nav-register" className="btn-brutal w-full">
                  Get Started
                </Link>
                <Link to="/login" data-testid="mobile-nav-login" className="btn-brutal secondary mt-3 w-full">
                  Sign In
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
